import React from 'react';
import Tulips from '../Tulips';
import CrystalBall from '../CrystalBall';
import { SITE_CONTENT } from '../../data/siteContent';
import { BookOpen, Sparkles, FileText, Book, Crown, Check } from 'lucide-react';
import coverImg from '../../assets/troquelada-cover.jpg';
import { localizeAmazonUrl, detectCountry } from '../../lib/amazonRouter';

const ICON_MAP = {
  ebook: FileText,
  'tapa-blanda': Book,
  troquelada: Crown
};

function EditionCard({ edition }) {
  const { id, title, subtitle, features, cta, featured, badge, url } = edition;
  const Icon = ICON_MAP[id] || Book;

  const isTroquelada = id === 'troquelada';
  const bgGradient = featured
    ? 'from-[#fbe4d4] via-[#f6d6cb] to-[#efc7d4]'
    : isTroquelada
      ? 'from-[#fbeed7] via-[#f4ddae] to-[#e9c78a]'
      : 'from-[#fbeed7] via-[#f4e4c8] to-[#eed6ae]';

  // Mobile display order: Tapa Dura (troquelada) -> Tapa Blanda -> E-book.
  // On sm+ breakpoints we reset to the natural order defined in siteContent.
  const mobileOrder =
    id === 'troquelada' ? 'order-1 sm:order-none'
    : id === 'tapa-blanda' ? 'order-2 sm:order-none'
    : 'order-3 sm:order-none';

  return (
    <div
      className={[
        'relative rounded-[22px] p-5 sm:p-6 flex flex-col items-center text-center border border-[#ead6b4]/50',
        mobileOrder,
        'bg-gradient-to-b', bgGradient,
        'shadow-[0_18px_40px_-22px_rgba(120,80,60,0.35)]',
        'transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_26px_50px_-22px_rgba(120,80,60,0.45)]',
        featured ? 'md:-translate-y-3' : ''
      ].join(' ')}
    >
      {badge && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[0.65rem] tracking-[0.2em] uppercase font-semibold text-white bg-gradient-to-r from-[#d7a1c4] to-[#b88bc8] shadow-md flex items-center gap-1 whitespace-nowrap">
          <Sparkles className="w-3 h-3" />
          {badge}
        </span>
      )}

      <div className="w-12 h-12 rounded-xl bg-white/60 flex items-center justify-center mb-3 border border-white/80">
        <Icon className="w-6 h-6 text-[#a27a57]" strokeWidth={1.5} />
      </div>

      <h3 className="font-serif-display text-[1.2rem] sm:text-[1.35rem] text-[#3f2e22]">{title}</h3>
      <p className="mt-1 text-[0.78rem] sm:text-[0.82rem] text-[#6b5440] font-body">{subtitle}</p>

      <ul className="mt-4 space-y-2 text-[0.78rem] sm:text-[0.82rem] text-[#5b4632] font-body w-full">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 justify-center leading-snug px-1">
            <Check className="w-3.5 h-3.5 text-[#a27a57] mt-[3px] shrink-0" strokeWidth={2} />
            <span className="text-left">{f}</span>
          </li>
        ))}
      </ul>

      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={async (e) => {
          // Localize URL based on visitor country (OneLink-style routing)
          try {
            const localized = await localizeAmazonUrl(url);
            if (localized && localized !== url) {
              e.preventDefault();
              window.open(localized, '_blank', 'noopener,noreferrer');
            }
          } catch (_) {
            // fall through to default href
          }
        }}
        className={[
          'mt-5 px-4 py-2.5 rounded-full text-white font-medium tracking-wide text-[0.78rem] sm:text-[0.82rem] whitespace-nowrap inline-block',
          'transition-[transform,box-shadow] duration-300 shadow-[0_10px_22px_-10px_rgba(155,120,190,0.6)]',
          'hover:-translate-y-0.5',
          featured
            ? 'bg-gradient-to-r from-[#e0a7c4] to-[#c28fb4]'
            : isTroquelada
              ? 'bg-gradient-to-r from-[#e6b76b] to-[#c99050]'
              : 'bg-gradient-to-r from-[#cdb2e0] to-[#b89dd4]'
        ].join(' ')}
      >
        {cta}
      </a>
    </div>
  );
}

export default function MiLibro() {
  const { title, bookName, subtitle, editions } = SITE_CONTENT.libro;
  // Pre-warm geographic detection so the click is instantaneous
  React.useEffect(() => { detectCountry(); }, []);
  return (
    <section className="w-full flex flex-col items-center">
      <Tulips />
      <div className="w-full max-w-6xl mx-auto">
        <h2 className="text-center font-serif-display text-[1.85rem] md:text-[2.1rem] text-[#3f2e22] flex items-center justify-center gap-3">
          <BookOpen className="w-6 h-6 text-[#c28fb4]" strokeWidth={1.6} />
          {title}
        </h2>
        <p className="mt-1 text-center font-serif-display italic text-[#8e6a4c] text-[1.15rem]">“{bookName}”</p>
        <p className="mt-2 text-center text-[#7a5d44] font-body text-[0.95rem] max-w-xl mx-auto">{subtitle}</p>

        <div className="mt-10 flex justify-center">
          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-to-tr from-[#cdb2e0]/30 via-[#e9c1d5]/30 to-[#e8d36a]/30 blur-2xl rounded-3xl" />
            <img
              src={coverImg}
              alt="Portada del libro Troquelada"
              className="relative w-[190px] sm:w-[220px] md:w-[240px] rounded-2xl shadow-[0_24px_50px_-18px_rgba(80,40,80,0.45)] border border-white/40 transition-transform duration-500 hover:scale-[1.02] hover:-rotate-1"
            />
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-5 md:gap-8 max-w-5xl mx-auto">
          {editions.map((ed) => (
            <EditionCard key={ed.id} edition={ed} />
          ))}
        </div>

        <div className="mt-4 flex justify-center">
          <CrystalBall size={66} animate={true} />
        </div>
      </div>
    </section>
  );
}
